import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useIntersection } from 'react-use';
import gfm from 'remark-gfm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLevelUp } from '@fortawesome/pro-solid-svg-icons';
import Loader from 'epn-ui/umd/dist/components/Loader';
import Button from 'epn-ui/umd/dist/components/Button';

import useI18n from '@i18n';

import Sidebar from '@components/pages/offers/[url]/Sidebar';
import OfferTable from '@components/pages/offers/[url]/Table';
import Demands from '@components/pages/offers/[url]/Demands';
import Coupons from '@components/pages/offers/[url]/Coupons';
import IncreasedRate from '@components/pages/offers/[url]/IncreasedRate';

import { AMPLITUDE_EVENTS_KEYS, logAmplitudeEvent } from '@utils/metrics/amplitude';
import sendGoal from '@utils/metrics/ymSendGoal';
import auth from '@config/auth.json';

import {
    getSortedComission,
    IComissionItem,
    IIncreasedComissionItem,
    formatCoupons,
} from '@components/pages/offers/[url]/config';
import useRefreshToken from '@hooks/useRefreshToken';
import { generateRoute } from '@utils/router';
import apiHooks from '@apiHooks/index';
import { IGetPlacementsDataFormatted } from '@api/types';
import useLocalStorage from '@hooks/useLocalStorage';

import { IOfferDetailtViewProps, IUniqPlacementOption } from './interfaces';
import OfferInfo from './OfferInfo';
import OrdInfo from './OrdInfo';
import Slogan from './Slogan';
import Dropdown from './Dropdown';
import InfoCards from './InfoCards';
import NeedToAddPlacementModal from '../Modals/NeedToAddPlacement';
import AddPlacementSentModal from '../Modals/AddPlacementSentModal';
import ConnectionModal from '../Modals/ConnectionModal';
import BanReasonModal from '../Modals/BanReasonModal';

const ReactMarkdown = dynamic(() => import('react-markdown'), {
    ssr: true,
});

const { usePlacementsUniqOffers } = apiHooks;

const OfferDetailView: React.FC<IOfferDetailtViewProps> = ({
    offerDetail,
    offerDetailError,
    isOfferDetailLoading,
    trafficSource,
    isLoadingTrafficSorces,
    coupons,
    isLoadingCoupons,
    couponsErrors,
    compilations,
    user,
}) => {
    const [intersectedOnce, setIntersectedOnce] = useState(false);
    const { t, locale } = useI18n('Offers');
    const { t: tCommon } = useI18n('Common');

    const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
    const [isPlacementsEmptyModal, setIsPlacementsEmptyModal] = useState(false);
    const [isAddPlacementSentModal, setIsAddPlacementSentModal] = useState(false);
    const [isBanReasonModal, setIsBanReasonModal] = useState(false);
    const [currentPlacement, setCurrentPlacement] = useState<IGetPlacementsDataFormatted>(null);
    const [defaultPlacementId] = useLocalStorage('offersPlacementId', null);

    const { data: placementsUniqData, mutate } = usePlacementsUniqOffers<
        IGetPlacementsDataFormatted[]
    >({
        params: {
            statuses: 'review,waiting,allow,ban,recheck',
            fields: 'title,url,hash,trafficSourceId,connections',
            limit: 50,
        },
        condition: !!user?.wmOnboarding?.hasPlacement,
        dependencies: [locale, user?.wmOnboarding?.hasPlacement],
    });

    const registrationLink = generateRoute(auth.REG_URL, { locale });

    const couponsRef = useRef(null);
    const mockRef = useRef(null);

    const isAuth = useRefreshToken();

    const onCouponsView = useCallback(() => {
        logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.offerCouponsView);
    }, []);

    const shouldLogIntersection = intersectedOnce || !coupons;

    const intersection = useIntersection(shouldLogIntersection ? mockRef : couponsRef, {
        root: null,
        rootMargin: '0px',
        threshold: 0.8,
    });

    const placementUniqOptions: IUniqPlacementOption[] = useMemo(
        () =>
            placementsUniqData?.map(placement => ({
                value: String(placement.id),
                label: placement.title || placement.url,
                info: placement,
            })) || [],
        [placementsUniqData],
    );

    const isDataLoading = isLoadingCoupons || isOfferDetailLoading;

    const isReqHasError = !!offerDetailError || !!couponsErrors;

    const formattedCoupons = useMemo(() => {
        if (!isDataLoading && !isReqHasError && coupons && offerDetail?.logoSmall) {
            return formatCoupons(coupons, offerDetail?.logoSmall);
        }
        return [];
    }, [coupons, isDataLoading, isReqHasError, offerDetail?.logoSmall]);

    const wmWarningTextConfig = offerDetail?.wmWarningText
        ? [
              {
                  key: 'Important',
                  link: 'note',
              },
          ]
        : [];
    const couponsConfig = coupons
        ? [
              {
                  key: 'Promotions and promo codes',
                  link: 'promotions',
              },
          ]
        : [];

    const sidebarConfig = [
        {
            key: 'Description',
            link: 'common',
        },
        ...wmWarningTextConfig,
        {
            key: 'Rates',
            link: 'rates',
        },
        {
            key: 'Conditions',
            link: 'conditions',
        },
        {
            key: 'Traffic source requirements',
            link: 'traffic',
        },

        ...couponsConfig,
    ];

    const conditionsData = [
        {
            title: t('Cookie life time'),
            value: offerDetail?.cookieLive,
        },
        {
            title: t('Average order confirmation time'),
            value: offerDetail?.wmConfirmTime,
        },
        {
            title: t('Geotargeting'),
            value: offerDetail?.geoTargetCountry?.join(', '),
            comment: offerDetail?.geoTargetComment,
        },
        {
            title: t('Tracking in a mobile application'),
            value: offerDetail?.canBuyInMobileApp === 'true' ? tCommon('Yes') : tCommon('No'),
        },
    ];

    const currentPercent = `${offerDetail?.webmasterMaxRate || ''}${
        offerDetail?.webmasterMaxRateSymbol || ''
    }`;

    const getParticularConnection = useMemo(() => {
        if (currentPlacement && currentPlacement?.connections) {
            return (
                currentPlacement?.connections?.find(
                    placement => +placement.offerId === +offerDetail.id,
                ) || null
            );
        }
        return null;
    }, [offerDetail.id, currentPlacement]);

    const onStoreClickHandler = () => {
        logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.offerCouponsGoToStore);
    };

    const onCodeCopyHandler = () => {
        logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.offerCouponsCopyCode);
    };

    const hasIncreasedRate =
        Array.isArray(offerDetail?.comission) && offerDetail?.comission.some(elem => elem.oldValue);

    const changeIncreasedRateValues = (rates: IComissionItem[]): IIncreasedComissionItem[] =>
        rates.map(rate => {
            if (rate.oldValue) {
                const newValue = <IncreasedRate value={rate.value} oldValue={rate.oldValue} />;
                return {
                    ...rate,
                    value: newValue,
                };
            }
            return rate;
        });

    const formatIncreasedRate = (rates: IComissionItem[]): IIncreasedComissionItem[] =>
        hasIncreasedRate ? changeIncreasedRateValues(rates) : rates;

    const redirectToSignup = () => {
        window.open(registrationLink);
    };

    const onConnect = () => {
        if (!isAuth) {
            redirectToSignup();
            return;
        }

        if (user?.wmOnboarding?.hasPlacement) {
            setIsConnectModalOpen(true);
        } else {
            setIsPlacementsEmptyModal(true);
        }
    };

    const getConnectionComment = () => {
        const foundedPlacementConnection = getParticularConnection;
        return foundedPlacementConnection !== null ? foundedPlacementConnection?.comment : '';
    };

    const onChangePlacement = (id: string) =>
        setCurrentPlacement(placementsUniqData?.find(option => option.id === id) || null);

    const updateCurrentPlacement = () =>
        setCurrentPlacement(placement => ({
            ...placement,
            connections: [
                ...placement.connections,
                { offerId: String(offerDetail.id), status: 'review' },
            ],
        }));

    useEffect(() => {
        if (placementsUniqData?.length && currentPlacement === null) {
            let selectedPlacement = null;
            if (defaultPlacementId)
                selectedPlacement = placementsUniqData.find(item => item.id === defaultPlacementId);
            setCurrentPlacement(selectedPlacement || placementsUniqData?.[0]);
        }
    }, [placementsUniqData, currentPlacement, defaultPlacementId]);

    useEffect(() => {
        if (!intersectedOnce && intersection?.intersectionRatio > 0.8) {
            setIntersectedOnce(true);
            onCouponsView();
        }
    }, [intersection, intersectedOnce, onCouponsView]);

    useEffect(() => {
        setIntersectedOnce(false);
    }, [coupons]);

    useEffect(() => {
        if (isAuth) {
            sendGoal('view_offer_page', 89120816);
        }
    }, [isAuth]);

    return (
        <div className="mb-20">
            <Head>
                <title>
                    {t('affiliate program with up to revenue', {
                        name: offerDetail.name,
                        percent: currentPercent,
                        currentYear: new Date().getFullYear(),
                    })}
                </title>
                <meta
                    name="description"
                    content={t(
                        'Official affiliate program by ePN with high commission rates up to for affiliates, webmasters and bloggers',
                        {
                            name: offerDetail.name,
                            percent: currentPercent,
                        },
                    )}
                />

                <meta
                    property="og:title"
                    content={t('Official affiliate program by ePN with profit up to', {
                        name: offerDetail.name,
                        percent: currentPercent,
                    })}
                />

                <meta
                    property="og:description"
                    content={t(
                        'Official affiliate program by ePN with high commission rates up to for affiliates, webmasters and bloggers',
                        {
                            name: offerDetail.name,
                            percent: currentPercent,
                        },
                    )}
                />
            </Head>
            <div className="flex flex-col overflow-hidden lg:flex-row gap-6 xs:gap-8">
                <aside className="flex flex-col gap-4 xl:min-w-[328px] lg:min-w-[253px] xl:max-w-[328px] lg:max-w-[253px]">
                    <OfferInfo
                        image={offerDetail.image}
                        link={offerDetail.linkDefault}
                        name={offerDetail.name}
                        percent={currentPercent}
                        compilationIds={offerDetail.compilationIds}
                        compilations={compilations}
                        ordClientFullName={offerDetail.ordClientFullName}
                        ordClientInn={offerDetail.ordClientInn}
                        offerId={offerDetail?.id}
                        connection={getParticularConnection}
                        placementUniqOptions={placementUniqOptions}
                        currentPlacement={currentPlacement}
                        onChangePlacement={onChangePlacement}
                        onConnect={onConnect}
                        onAuth={redirectToSignup}
                        onShowReason={() => setIsBanReasonModal(true)}
                    />
                    <Sidebar config={sidebarConfig} />
                    <div className="hidden lg:block">
                        <OrdInfo
                            ordClientFullName={offerDetail.ordClientFullName}
                            ordClientInn={offerDetail.ordClientInn}
                        />
                    </div>
                </aside>

                <div className="flex flex-col w-full gap-2 xl:min-w-[1048px] lg:min-w-[823px] xl:max-w-[1048px] lg:max-w-[823px]">
                    <InfoCards
                        confirm={offerDetail?.confirm}
                        cr={offerDetail?.cr}
                        confirmDays={offerDetail?.webmasterConfirmTimeInDays}
                        cookieLive={offerDetail?.cookieLive}
                        maxRate={offerDetail?.webmasterMaxRate}
                        maxRateSymbol={offerDetail?.webmasterMaxRateSymbol}
                    />
                    {offerDetail?.slogan && <Slogan text={offerDetail.slogan} />}

                    <div id="common">
                        <Dropdown title={t('Description')}>
                            <ReactMarkdown
                                remarkPlugins={[gfm]}
                                components={{
                                    a: props => (
                                        <a
                                            href={props.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {props.children}
                                        </a>
                                    ),
                                }}
                                className="hack-markdown-syles"
                            >
                                {offerDetail.wmDescription}
                            </ReactMarkdown>
                        </Dropdown>
                    </div>

                    {offerDetail.wmWarningText && (
                        <div id="note">
                            <Dropdown title={t('Important')}>
                                <ReactMarkdown
                                    remarkPlugins={[gfm]}
                                    className="hack-markdown-syles"
                                    components={{
                                        a: props => (
                                            <a
                                                href={props.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {props.children}
                                            </a>
                                        ),
                                    }}
                                >
                                    {offerDetail.wmWarningText}
                                </ReactMarkdown>
                            </Dropdown>
                        </div>
                    )}

                    <div id="rates">
                        <Dropdown
                            title={
                                <div className="flex flex-row items-center">
                                    {t('Rates')}
                                    {hasIncreasedRate && (
                                        <div className="text-red-400 text-p1">
                                            <FontAwesomeIcon
                                                id="increasedRate"
                                                className="ml-[16px] mr-[8px]"
                                                icon={faLevelUp}
                                            />
                                            <span className="font-normal">{t('Raise')}</span>
                                        </div>
                                    )}
                                </div>
                            }
                        >
                            <OfferTable
                                data={formatIncreasedRate(
                                    getSortedComission(offerDetail.comission),
                                )}
                            />
                        </Dropdown>
                    </div>

                    <div id="conditions">
                        <Dropdown title={t('Conditions')}>
                            <OfferTable data={conditionsData} />
                        </Dropdown>
                    </div>
                    <div id="traffic">
                        <Dropdown title={t('Traffic source requirements')}>
                            {isLoadingTrafficSorces ? (
                                <Loader />
                            ) : (
                                <Demands
                                    demands={offerDetail.demands}
                                    trafficSource={trafficSource || []}
                                />
                            )}
                        </Dropdown>
                    </div>

                    {coupons && (
                        <div ref={couponsRef} id="promotions">
                            <Dropdown title={t('Promotions and promo codes')}>
                                <Coupons
                                    coupons={formattedCoupons}
                                    isDataLoading={isDataLoading}
                                    isReqHasError={isReqHasError}
                                    onStoreClickHandler={onStoreClickHandler}
                                    onCodeCopyHandler={onCodeCopyHandler}
                                />
                            </Dropdown>
                        </div>
                    )}

                    {!getParticularConnection && (
                        <div className="flex flex-col items-center mt-8 xs:mt-10">
                            <p className="mb-6 font-medium text-center xs:mt-8 xs:max-w-[500px] text-blueGray-800 text-[18px] xs:text-[24px] leading-[27px] xs:leading-[35px]">
                                {t('affiliate program — earn up to from an order', {
                                    name: offerDetail.name,
                                    percent: currentPercent,
                                })}
                            </p>
                            <Button
                                className="w-full xs:w-[220px] !bg-blueGray-800 hover:!bg-blueGray-600"
                                type="primary"
                                onClick={onConnect}
                            >
                                {isAuth ? tCommon('Connect') : t('Get started')}
                            </Button>
                        </div>
                    )}

                    {isConnectModalOpen && (
                        <ConnectionModal
                            isOpen={isConnectModalOpen}
                            onClose={() => {
                                setIsConnectModalOpen(false);
                            }}
                            offerName={offerDetail?.title || offerDetail?.name || null}
                            offerId={offerDetail?.id}
                            placements={placementUniqOptions}
                            onConnectionSuccess={() => {
                                updateCurrentPlacement();
                                mutate();
                                setIsConnectModalOpen(false);
                                setIsAddPlacementSentModal(true);
                            }}
                            currentPlacementValue={currentPlacement?.id}
                        />
                    )}

                    {isAddPlacementSentModal && (
                        <AddPlacementSentModal
                            isOpen={isAddPlacementSentModal}
                            onClose={() => setIsAddPlacementSentModal(false)}
                            closeButtonText={t('Stay on the offer page')}
                        />
                    )}

                    {isPlacementsEmptyModal && (
                        <NeedToAddPlacementModal
                            isOpen={isPlacementsEmptyModal}
                            onClose={() => setIsPlacementsEmptyModal(false)}
                        />
                    )}

                    {isBanReasonModal && (
                        <BanReasonModal
                            isOpen={isBanReasonModal}
                            reason={getConnectionComment()}
                            onClose={() => {
                                setIsBanReasonModal(false);
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default OfferDetailView;
